<template>
  <div>
    <Nav></Nav>
    <b-container fluid class="mt-3">
      <b-row>
        <b-col>
          <h1>Recetas para manufactura</h1>
          <p class="text-muted">Gestiona las listas de materiales (BOMs) para la producción</p>
        </b-col>
      </b-row>
      
      <b-row class="mb-3">
        <b-col cols="12">
          <b-button variant="success" size="sm" @click="openCreateModal">
            <i class="fas fa-plus"></i> Crear Receta de Manufactura
          </b-button>
          <b-button variant="info" size="sm" class="ml-2" @click="getBoms">
            <i class="fas fa-sync-alt"></i> Actualizar Lista
          </b-button>
        </b-col>
      </b-row>

      <b-row class="mb-3">
        <b-col lg="6" md="8" sm="12">
          <b-form-group label="Buscar:" label-for="search-input">
            <b-form-input 
              id="search-input"
              type="search" 
              v-model="routeQuery.search" 
              @keyup="page = 1"
              debounce="500"
              placeholder="Buscar por nombre, ID o producto..."
            ></b-form-input>
          </b-form-group>
        </b-col>
        <b-col lg="3" md="4" sm="12">
          <b-form-group label="Estado:" label-for="status-filter">
            <b-form-select 
              id="status-filter"
              v-model="statusFilter" 
              :options="statusOptions"
              @change="filterBoms"
            ></b-form-select>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <b-table 
            ref="selectableTable" 
            selectable 
            @row-selected="onRowSelected" 
            small 
            :filter="routeQuery.search"
            striped 
            hover 
            :items="filteredBoms" 
            :fields="bomsLabels" 
            responsive
            :busy="loading"
            show-empty
            empty-text="No se encontraron recetas de manufactura"
          >
            <template #table-busy>
              <div class="text-center text-primary my-4">
                <b-spinner class="align-middle"></b-spinner>
                <strong class="ml-2">Cargando recetas...</strong>
              </div>
            </template>

            <template #cell(active)="data">
              <b-badge :variant="data.item.active ? 'success' : 'danger'">
                {{ data.item.active ? 'Activo' : 'Inactivo' }}
              </b-badge>
            </template>

            <template #cell(supply_id)="data">
              <b-badge variant="info">{{ data.item.supply_id }}</b-badge>
            </template>

            <template #cell(product_qty)="data">
              {{ data.item.product_qty }} {{ getSupplyUnit(data.item.supply_id) }}
            </template>

            <template #cell(create_date)="data">
              {{ formatDate(data.item.create_date) }}
            </template>

            <template #cell(sequence)="data">
              <b-badge variant="secondary">{{ data.item.sequence }}</b-badge>
            </template>

            <template #cell(buttons)="data">
              <b-button-group size="sm">
                <b-button 
                  variant="success" 
                  @click="$router.push('/bom/' + data.item.id)" 
                  class="mr-1"
                >
                  <i class="fas fa-eye"></i> Ver Detalles
                </b-button>
                <b-button 
                  variant="primary" 
                  @click="editBom(data.item)"
                  :disabled="!data.item.active"
                >
                  <i class="fas fa-edit"></i> Editar
                </b-button>
              </b-button-group>
            </template>
          </b-table>
        </b-col>
      </b-row>

      <!-- Create/Edit BOM Modal -->
      <b-modal 
        id="modal-newBom" 
        :title="editingBom ? 'Editar Receta de Manufactura' : 'Crear Nueva Receta de Manufactura'"
        size="lg"
        @ok="handleBomSubmit"
        @hidden="resetModal"
        :ok-disabled="!isFormValid"
        ok-title="Guardar"
        cancel-title="Cancelar"
      >
        <b-form ref="bomForm">
          <b-row>
            <b-col md="6">
              <b-form-group 
                label="Nombre de la Receta *" 
                label-for="bom-name"
                :invalid-feedback="errors.name"
                :state="getValidationState('name')"
              >
                <b-form-input 
                  id="bom-name"
                  v-model="newBom.name" 
                  :state="getValidationState('name')"
                  placeholder="Ej: Receta Café Preparado 420gr"
                  required
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group 
                label="Producto Final *" 
                label-for="supply-select"
                :invalid-feedback="errors.supply_id"
                :state="getValidationState('supply_id')"
              >
                <b-form-select 
                  id="supply-select"
                  v-model="newBom.supply_id" 
                  :options="suppliesOptions"
                  :state="getValidationState('supply_id')"
                  required
                >
                  <template #first>
                    <b-form-select-option value="" disabled>
                      -- Seleccionar Producto --
                    </b-form-select-option>
                  </template>
                </b-form-select>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="4">
              <b-form-group 
                label="Cantidad Base *" 
                label-for="product-qty"
                :invalid-feedback="errors.product_qty"
                :state="getValidationState('product_qty')"
              >
                <b-form-input 
                  id="product-qty"
                  v-model.number="newBom.product_qty" 
                  type="number" 
                  step="0.01"
                  min="0.01"
                  :state="getValidationState('product_qty')"
                  placeholder="1.00"
                  required
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group 
                label="Secuencia" 
                label-for="sequence"
              >
                <b-form-input 
                  id="sequence"
                  v-model.number="newBom.sequence" 
                  type="number"
                  min="1"
                  placeholder="1"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="Estado">
                <b-form-checkbox 
                  v-model="newBom.active" 
                  switch
                  size="lg"
                >
                  {{ newBom.active ? 'Activo' : 'Inactivo' }}
                </b-form-checkbox>
              </b-form-group>
            </b-col>
          </b-row>

          <b-alert 
            :show="!!selectedSupply" 
            variant="info" 
            class="mt-3"
          >
            <h6>Información del Producto Seleccionado:</h6>
            <div v-if="selectedSupply">
              <strong>Código:</strong> {{ selectedSupply.code }} <br>
              <strong>Descripción:</strong> {{ selectedSupply.description }} <br>
              <strong>Unidad:</strong> {{ selectedSupply.unit }} <br>
              <strong>Precio:</strong> ${{ selectedSupply.price }}
            </div>
          </b-alert>
        </b-form>

        <template #modal-footer="{ ok, cancel }">
          <b-button variant="secondary" @click="cancel()">
            Cancelar
          </b-button>
          <b-button 
            variant="success" 
            @click="ok()" 
            :disabled="!isFormValid || saving"
          >
            <b-spinner small v-if="saving"></b-spinner>
            <i class="fas fa-save" v-else></i>
            {{ saving ? 'Guardando...' : 'Guardar Receta' }}
          </b-button>
        </template>
      </b-modal>
    </b-container>
  </div>
</template>

<script>
import axios from "axios";
import Nav from "../components/Nav.vue";

export default {
  name: "SuppliesBoms",
  components: {
    Nav
  },

  data() {
    return {
      loading: false,
      saving: false,
      editingBom: false,
      routeQuery: {
        page: null,
        search: "",
        fini: null,
        ffin: null,
        sucursales: null
      },
      statusFilter: 'all',
      boms: [],
      supplies: [],
      newBom: {
        name: "",
        supply_id: null,
        active: true,
        sequence: 1,
        product_qty: 1
      },
      errors: {},
      bomsLabels: [
        { key: "id", label: "ID", sortable: true },
        { key: "name", label: "Nombre", sortable: true },
        { key: "supply_id", label: "Producto ID", sortable: true },
        { key: "product_qty", label: "Cantidad", sortable: true },
        { key: "sequence", label: "Secuencia", sortable: true },
        { key: "active", label: "Estado", sortable: true },
        { key: "create_date", label: "Creación", sortable: true },
        { key: "buttons", label: "Acciones" }
      ],
      statusOptions: [
        { value: 'all', text: 'Todos los estados' },
        { value: 'active', text: 'Solo Activos' },
        { value: 'inactive', text: 'Solo Inactivos' }
      ]
    };
  },

  computed: {
    suppliesOptions() {
      return this.supplies.map(supply => ({
        value: supply.id,
        text: `${supply.code} - ${supply.name}`,
        supply: supply
      }));
    },

    selectedSupply() {
      return this.supplies.find(supply => supply.id === this.newBom.supply_id);
    },

    filteredBoms() {
      if (this.statusFilter === 'all') {
        return this.boms;
      }
      const isActive = this.statusFilter === 'active';
      return this.boms.filter(bom => bom.active === isActive);
    },

    isFormValid() {
      return this.newBom.name && 
             this.newBom.supply_id && 
             this.newBom.product_qty > 0 &&
             Object.keys(this.errors).length === 0;
    }
  },

  mounted() {
    this.loadData();
  },

  methods: {
    async loadData() {
      this.loading = true;
      try {
        await Promise.all([
          this.getBoms(),
          this.getSupplies()
        ]);
      } catch (error) {
        this.makeToast("danger", "Error", "Error al cargar los datos");
      } finally {
        this.loading = false;
      }
    },

    onRowSelected(item) {
      if (item.length > 0) {
        this.$router.push('/bom/' + item[0].id);
      }
      this.$refs.selectableTable.clearSelected();
    },

    getBoms() {
      return axios.get("/boms")
        .then(res => {
          this.boms = res.data.sort((a, b) => {
            // Sort by sequence first, then by name
            if (a.sequence !== b.sequence) {
              return a.sequence - b.sequence;
            }
            return a.name.localeCompare(b.name);
          });
        })
        .catch(err => {
          console.error("Error loading BOMs:", err);
          this.makeToast("danger", "Error", "Error al cargar las recetas");
        });
    },

    getSupplies() {
      return axios.get("/supplies")
        .then(res => {
          this.supplies = res.data;
        })
        .catch(err => {
          console.error("Error loading supplies:", err);
          this.makeToast("danger", "Error", "Error al cargar los productos");
        });
    },

    openCreateModal() {
      this.editingBom = false;
      this.resetBomForm();
      this.$bvModal.show('modal-newBom');
    },

    editBom(bom) {
      this.editingBom = true;
      this.newBom = { ...bom };
      this.$bvModal.show('modal-newBom');
    },
    resetBomForm() {
      this.newBom = {
        name: "",
        supply_id: null,
        active: true,
        sequence: this.getNextSequence(),
        product_qty: 1
      };
      this.errors = {};
    },

    resetModal() {
      this.resetBomForm();
      this.editingBom = false;
      this.saving = false;
    },

    getNextSequence() {
      if (this.boms.length === 0) return 1;
      return Math.max(...this.boms.map(bom => bom.sequence || 0)) + 1;
    },

    validateForm() {
      this.errors = {};

      if (!this.newBom.name || this.newBom.name.length < 3) {
        this.errors.name = "El nombre debe tener al menos 3 caracteres";
      }

      if (!this.newBom.supply_id) {
        this.errors.supply_id = "Debe seleccionar un producto";
      }

      if (!this.newBom.product_qty || this.newBom.product_qty <= 0) {
        this.errors.product_qty = "La cantidad debe ser mayor a 0";
      }

      // Check for duplicate supply_id (only for creation or different BOM)
      if (this.newBom.supply_id) {
        const existingBom = this.boms.find(bom => 
          bom.supply_id === this.newBom.supply_id && 
          (!this.editingBom || bom.id !== this.newBom.id)
        );
        if (existingBom) {
          this.errors.supply_id = "Ya existe una receta para este producto";
        }
      }

      return Object.keys(this.errors).length === 0;
    },

    getValidationState(field) {
      if (field in this.errors) {
        return false;
      }
      if (field === 'name' && this.newBom.name) {
        return this.newBom.name.length >= 3;
      }
      if (field === 'supply_id' && this.newBom.supply_id) {
        return true;
      }
      if (field === 'product_qty' && this.newBom.product_qty) {
        return this.newBom.product_qty > 0;
      }
      return null;
    },

    async handleBomSubmit() {
      if (!this.validateForm()) {
        return;
      }

      this.saving = true;
      try {
        const bomData = {
          name: this.newBom.name,
          supply_id: this.newBom.supply_id,
          active: this.newBom.active,
          sequence: this.newBom.sequence || 1,
          product_qty: this.newBom.product_qty
        };

        let response;
        if (this.editingBom && this.newBom.id) {
          response = await axios.put(`/bom/${this.newBom.id}`, bomData);
          this.makeToast("success", "Éxito", "Receta actualizada correctamente");
        } else {
          response = await axios.post("/bom", bomData);
          this.makeToast("success", "Éxito", "Receta creada correctamente");
        }

        await this.getBoms();
        this.$bvModal.hide('modal-newBom');

        // Navigate to the BOM detail page
        if (response.data.id) {
          this.$router.push('/bom/' + response.data.id);
        }

      } catch (error) {
        console.error("Error saving BOM:", error);
        this.makeToast("danger", "Error", 
          error.response?.data || "Error al guardar la receta");
      } finally {
        this.saving = false;
      }
    },

    filterBoms() {
      // Filtering is handled by computed property
    },

    getSupplyUnit(supplyId) {
      const supply = this.supplies.find(s => s.id === supplyId);
      return supply ? supply.unit : '';
    },

    formatDate(dateStr) {
      if (!dateStr) return '-';
      return new Date(dateStr).toLocaleDateString();
    },

    makeToast(variant = null, title = "", text = "") {
      if (variant === "success") {
        this.$bvToast.toast(text, {
          title: title,
          variant: variant,
          solid: true,
          appendToast: true,
        });
      } else {
        this.$bvModal.msgBoxOk(text, {
          title: title,
          okVariant: variant,
          centered: true
        });
      }
    },
  },
};
</script>

<style scoped>
.table th {
  background-color: #f8f9fa;
  font-weight: 600;
}

.btn-group .btn {
  margin-right: 2px;
}

.table td {
  vertical-align: middle;
}

.badge {
  font-size: 0.85em;
}

.form-group label {
  font-weight: 600;
  color: #495057;
}

.modal-header {
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
}

.text-muted {
  font-size: 0.9em;
}
</style>